import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PrefillType } from '@weave/schema-gen-ts/dist/schemas/payments-platform/care-credit-integration/care_credit_integration.pb';
import { PaymentsProp } from '@wetags/payments';
import { PersonsV3 } from '@frontend/api-person';
import { getUser } from '@frontend/auth-helpers';
import {
  useCareCreditPatientData,
  useCareCreditLocationStatus,
  useCareCreditRestrictionsCheck,
} from '@frontend/payments-care-credit';
import { PaymentsFeatureFlags, useLocationSurchargeState, useMerchants } from '@frontend/payments-hooks';
import { isPaidInvoice } from '@frontend/payments-invoice-calculations';
import { useInvalidateInvoices } from '@frontend/payments-invoice-controller';
import { useLoadJustifi } from '@frontend/payments-justifi-wrapper';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { useModalControl, useMultiStepControl } from '@frontend/design-system';
import { makeLifecycleHooks } from '../utils/lifecycle';
import {
  CollectPaymentMultiActiveGateway,
  UseCollectPaymentMultiStepProps,
  UseCollectPaymentMultiProps,
  CollectPaymentMultiFlow,
  CollectPaymentMultiState,
  CollectPaymentMultiSteps,
  CollectModalStateCareCreditPaymentStatus,
  CollectModalStateCareCreditEnrollmentStatus,
  CollectModalStateCareCreditMessageType,
} from './collect-payment-multi.types';
import { useInvoiceQuery } from './hooks/use-invoice-query';
import { useStepTrackingStoreShallow, useStepTrackingStoreUuid } from './utils/step-tracking-store';
import { getStepDataByFlow, isPaymentMethodStep, PAYMENT_MULTI_STEPS } from './utils/steps';

export const DEFAULT_MIN_WIDTH = 600;

export const useCollectPaymentMulti = <F extends CollectPaymentMultiFlow = 'create'>(
  collectProps: UseCollectPaymentMultiProps<F>,
  multiStepProps: UseCollectPaymentMultiStepProps = {}
): CollectPaymentMultiState<F> => {
  const {
    personId,
    invoiceId: initialInvoiceId,
    locationId: overrideLocationId,
    amount,
    onPaymentSuccess: onPaymentSuccessCallback,
    lifecycle,
    onInvoiceCreated,
    onInvoiceSelected,
    onPaymentCompletedAsync,
    onLocationHasNoPayments,
    trackingId = 'payments:nwx:collect-payment-multi',
    origin,
    isResponsiveMode: isResponsiveModeProp,
    topOffset: topOffsetProp,
    closeOnCareCreditVerification,
    hideBackButtonOnCareCreditVerification,
    hideBackButtonOnCareCreditCollect,
    ...rest
  } = collectProps || {};

  const [minWidth, setMinWidth] = useState(DEFAULT_MIN_WIDTH);
  const [isFocused, setIsFocused] = useState(false);
  const [isResponsiveMode, setIsResponsiveMode] = useState(isResponsiveModeProp || false);
  const [topOffset, setTopOffset] = useState(topOffsetProp || 0);
  const hasResponsiveModeProp = typeof isResponsiveModeProp === 'boolean';

  const { getFeatureFlagValue } = useAppFlagStore();

  const { uuid: modalInstanceUuid } = useStepTrackingStoreUuid();

  const { initiateModalState, finalizeModalState } = useStepTrackingStoreShallow(
    'initiateModalState',
    'finalizeModalState'
  );

  const resetMinWidth = useCallback(() => {
    setMinWidth(DEFAULT_MIN_WIDTH);
  }, []);

  const initialFlow = rest.flow || 'create';
  const initializedFlowRef = useRef<{ flow: CollectPaymentMultiFlow; initialActiveStep?: string }>();

  const beforeUnmountCallback = useCallback(() => {
    finalizeModalState({
      status: 'cancel',
    });
  }, [finalizeModalState]);

  const multiStep = useMultiStepControl({
    ...multiStepProps,
    isResponsiveMode,
    stepper: {
      show: false,
      ...multiStepProps?.stepper,
    },
    onChangeStep: (step, move) => {
      if (typeof multiStepProps.onChangeStep === 'function') {
        const proceed = multiStepProps.onChangeStep(step, move);

        if (proceed === false) {
          return;
        }
      }

      if (step === PAYMENT_MULTI_STEPS.selectTerminal && move.movement === -1) {
        // allow redirect for register terminal > select terminal.
        // Do nothing
      } else if (isPaymentMethodStep(step) && move.movement < 0) {
        // going back from payment step, go to payment methods
        multiStep.goPrevStep({ orStep: PAYMENT_MULTI_STEPS.paymentMethods });
        return false;
      }

      return;
    },
    onCancel: () => {
      if (typeof multiStepProps.onCancel === 'function') {
        multiStepProps.onCancel();
      }

      /**
       * custom logic here
       */
      beforeUnmountCallback();
    },

    onComplete: () => {
      if (typeof multiStepProps.onComplete === 'function') {
        multiStepProps.onComplete();
      }

      /**
       * custom logic here
       */
      finalizeModalState({
        status: 'complete',
      });
    },
  });

  const [activeInvoiceId, setActiveInvoiceId] = useState<string | null>(initialInvoiceId ?? null);
  const [flow, setFlow] = useState<CollectPaymentMultiFlow>(initialFlow);
  const [hasCompletedPayment, setHasCompletedPayment] = useState(false);

  const { accessibleLocationIds: allLocations, selectedLocationIds } = useAppScopeStore();
  const selectedLocationId = selectedLocationIds[0];

  /**
   * If we have override we don't want to query an invoice from other locations.
   *
   * Otherwise, prioritize selected location, but use all locations. Then if given an invoice ID
   * from another location, we can query it. The invoice's location will then become the highest priority.
   */
  const activeLocationIds = useMemo(() => {
    if (overrideLocationId) {
      return [overrideLocationId];
    }
    /**
     * MultiQueryUtils > LocationID has better context than allLocations.
     * allLocations should always be consulted last.
     */
    return [selectedLocationId, ...(allLocations || [])].filter(Boolean) as string[];
  }, [allLocations, selectedLocationId, overrideLocationId]);

  const { invoice, ...invoiceQuery } = useInvoiceQuery(activeInvoiceId, activeLocationIds);

  const activeLocationId = useMemo(() => {
    return invoice?.locationId || overrideLocationId || selectedLocationId;
  }, [overrideLocationId, selectedLocationId, invoice?.locationId]);

  /**
   * Refresh invoice lists once when the flow closes (unmounts), instead of mid-flow.
   * Only if an invoice was created/selected during the flow.
   */
  const invalidateInvoices = useInvalidateInvoices();
  const invalidateOnCloseRef = useRef({ invalidateInvoices, activeInvoiceId, activeLocationId });
  invalidateOnCloseRef.current = { invalidateInvoices, activeInvoiceId, activeLocationId };

  useEffect(() => {
    return () => {
      const { invalidateInvoices, activeInvoiceId, activeLocationId } = invalidateOnCloseRef.current;
      if (activeInvoiceId) {
        invalidateInvoices(activeLocationId);
      }
    };
  }, []);

  /* TODO: we want to use only one useMerchants call below, but we need to refactor the logic to check
  if the activeLocationId is in the selectedLocationIds and prepend it to the list if it is not.
  The above has to be done as the activeLocationId from invoice.locationId might not be
  in the selectedLocationIds if we land on the invoice details page directly using a link or
  if we get the invoice from a list based on a personId. */
  const { allMerchantsLoaded, anyLocationHasPayments } = useMerchants('selectedLocationIds');
  const { stripeLocationId, merchant, stripeCreatedNotOnboarded, isDemoLocation } =
    useMerchants(activeLocationId)['firstLocation'];

  const activeProcessor = useMemo(() => {
    return merchant?.processors?.find((p) => p.isActive);
  }, [merchant]);

  const paymentSetupModalControl = useModalControl();

  const activePersonId = invoice?.person?.id || personId;

  const activeLocationIdProp = useMemo(() => {
    return activeLocationId ? [activeLocationId] : [];
  }, [activeLocationId]);

  const [isServerDrivenV2Enabled] = PaymentsProp.useWetag(
    'enable-invoice-transition-to-pending-state',
    activeLocationId
  );

  const hasJustifyOnboardingEnabled = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.EnableJustifyOnboarding,
    locationIds: activeLocationIdProp,
  });

  const [careCreditPaymentStatus, setCareCreditPaymentStatus] = useState<
    CollectModalStateCareCreditPaymentStatus | undefined
  >(undefined);
  const [careCreditEnrollmentStatus, setCareCreditEnrollmentStatus] = useState<
    CollectModalStateCareCreditEnrollmentStatus | undefined
  >(undefined);
  const [careCreditMessageType, setCareCreditMessageType] = useState<
    CollectModalStateCareCreditMessageType | undefined
  >(undefined);

  const { data: person, ...personQuery } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    { locationIds: activeLocationIdProp, personId: activePersonId! },
    { enabled: !!activePersonId && !!activeLocationId }
  );

  const {
    showAnyCareCreditUI,
    showCareCreditDecoupled,
    merchantNumber,
    isAnyLoading: isCareCreditLocationStatusLoading,
  } = useCareCreditLocationStatus(activeLocationId);

  const { isCareCreditRestrictedState } = useCareCreditRestrictionsCheck({
    locationId: activeLocationId,
    isCareCreditEnabled: showAnyCareCreditUI,
  });

  const getCareCreditPurlPrefilType = (): PrefillType => {
    if (careCreditMessageType === 'payment-link') {
      return PrefillType.PREFILL_TYPE_TRANSACTION_PROCESS;
    }

    if (careCreditMessageType === 'enrollment-link' || careCreditMessageType === 'pre-approved-link') {
      return PrefillType.PREFILL_TYPE_APPLICATION_PROCESS;
    }

    return PrefillType.PREFILL_TYPE_UNSPECIFIED;
  };

  const userId = getUser()?.userID || '';

  const {
    careCreditData,
    careCreditChipStatus,
    isLoading: isCareCreditDataLoading,
  } = useCareCreditPatientData(
    {
      person,
      invoice,
      merchantNumber,
      locationId: activeLocationId,
      userId,
      isDecoupled: showCareCreditDecoupled,
    },
    {
      enabled: showAnyCareCreditUI,
      prefillType: getCareCreditPurlPrefilType(),
    }
  );

  const enableACHOnFileStripe = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.ACHOnFile,
    locationIds: activeLocationIdProp,
  });

  const enableShareInEmail = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.ShareInEmail,
    locationIds: [invoice?.locationId ?? ''],
  });

  const { isAllEnabled: surchargeEnabled, firstPercentValue: surchargePercentValue } =
    useLocationSurchargeState(activeLocationIdProp);

  const activeGateway: CollectPaymentMultiActiveGateway = useMemo(() => {
    if (hasJustifyOnboardingEnabled && activeProcessor?.processorType?.toLowerCase() === 'justifi') {
      return 'justifi';
    }
    if (stripeLocationId) {
      return 'stripe';
    }

    // show "Stripe account not setup" errors.
    return 'stripe';
  }, [stripeLocationId, hasJustifyOnboardingEnabled, activeProcessor]);

  const changeFlow = useCallback(
    (newFlow: CollectPaymentMultiFlow, goToStepName?: string) => {
      setFlow(newFlow);
      const steps = getStepDataByFlow(newFlow, multiStep.steps);

      multiStep.setSteps(steps);

      if (goToStepName) {
        multiStep.setActiveStep(goToStepName);
      } else if (steps[0]) {
        multiStep.setActiveStep(steps[0].id);
      }
    },
    [initialInvoiceId, multiStep.steps]
  );

  const resetState = useCallback(() => {
    setActiveInvoiceId(initialInvoiceId ?? null);
    changeFlow(initialFlow);
  }, [initialInvoiceId, multiStep.steps]);

  useEffect(() => {
    /**
     * Prevent changeFlow() forom being called for the same initial flow and active step.
     */
    if (
      initializedFlowRef.current?.flow === initialFlow &&
      initializedFlowRef.current?.initialActiveStep === multiStepProps.initialActiveStep
    ) {
      return;
    }

    initializedFlowRef.current = {
      flow: initialFlow,
      initialActiveStep: multiStepProps.initialActiveStep,
    };
    changeFlow(initialFlow, multiStepProps.initialActiveStep);
  }, [changeFlow, initialFlow, multiStepProps.initialActiveStep]);

  useEffect(() => {
    if (!!initialInvoiceId && flow === 'create') {
      console.error(
        'useCollectPaymentMulti: props.invoiceId should not be used in "create" flow. If you are managing your own selectedInvoiceId state, please reset it inside your "onClickCreateInvoice" handler.'
      );
    }
    setActiveInvoiceId(initialInvoiceId ?? null);
  }, [initialInvoiceId]);

  const renderSelectPaymentsTemplate =
    collectProps.flow === 'insert' ? collectProps.renderSelectPaymentsTemplate : undefined;

  const onPaymentSuccess = useCallback(() => {
    setHasCompletedPayment(true);

    if (typeof onPaymentSuccessCallback === 'function') {
      onPaymentSuccessCallback();
    }
  }, [onPaymentSuccessCallback]);

  const invoiceIsPaid = isPaidInvoice(invoice);

  const lifecycleHooks = useMemo(() => {
    return makeLifecycleHooks(lifecycle);
  }, [lifecycle]);

  useEffect(() => {
    if (!isFocused) {
      /**
       * Prevent errors if hook is being invoked without being active
       */
      return;
    }
    if (invoiceIsPaid && !hasCompletedPayment && multiStep.activeStep !== PAYMENT_MULTI_STEPS.collectTerminal) {
      if (careCreditEnrollmentStatus?.status === 'declined') {
        /**
         * Invoice got paid outside of flow.
         * Remove any declined messaging.
         */
        setCareCreditEnrollmentStatus(undefined);
        setCareCreditMessageType(undefined);
      }
      if (typeof onPaymentCompletedAsync == 'function') {
        setHasCompletedPayment(true);
        onPaymentCompletedAsync(invoice);
      } else {
        if (multiStep.steps.length > 0) {
          if (flow === 'insert') {
            multiStep.goToStep({ orIndex: 0 });
          } else if (multiStep.activeStep !== PAYMENT_MULTI_STEPS.paymentMethods) {
            multiStep.goToStep({ orStep: PAYMENT_MULTI_STEPS.paymentMethods });
          }
        }
        onPaymentSuccess();
      }
    }
  }, [invoiceIsPaid, isFocused]);

  useLoadJustifi(['paymentMethod'], activeGateway === 'justifi' && isFocused);

  useEffect(() => {
    /**
     * When this modal instance is focused (rendered) we want to initiate the modal state.
     * This is because multiple instances of useCollectPaymentMulti() run at once
     * or not.
     */
    if (isFocused) {
      initiateModalState({
        uuid: modalInstanceUuid,
        flow,
        firstStep: multiStep.activeStep as CollectPaymentMultiSteps,
        activeGateway,
      });
    }
  }, [isFocused]);

  return {
    // direct callback passthrough
    ...rest,
    onInvoiceCreated,
    onInvoiceSelected,
    renderSelectPaymentsTemplate,
    onLocationHasNoPayments,

    // wrapped callbacks
    onPaymentSuccess,
    beforeUnmountCallback,

    // // behind the scenes methods,
    resetState,

    // // direct prop passthrough
    trackingId,
    multiStep,
    origin,
    amount,

    // // controlled state
    activeInvoiceId,
    setActiveInvoiceId,
    modalInstanceUuid,
    flow,
    setFlowAndStep: changeFlow,
    minWidth,
    setMinWidth,
    resetMinWidth,
    isFocused,
    setIsFocused,

    // derived state
    invoice,
    invoiceQuery,
    invoiceIsPaid,
    hasCompletedPayment,

    activeGateway,
    activePersonId,
    person,
    personQuery,
    activeLocationIds,
    activeLocationId,
    merchant,
    activeProcessor,
    isDemoLocation,
    allMerchantsLoaded,
    anyLocationHasPayments,
    paymentSetupModalControl,
    surchargeEnabled,
    surchargePercentValue,
    stripeData: {
      stripeLocationId,
      enableACHOnFile: enableACHOnFileStripe,
      enableShareInEmail,
      stripeCreatedNotOnboarded,
      canRegisterTerminals: true,
    },
    justifiData: {
      hasOnboardingEnabled: hasJustifyOnboardingEnabled,
      enableShareInEmail,
      canRegisterTerminals: true,
    },
    lifecycleHooks,
    isResponsiveMode,
    setIsResponsiveMode,
    topOffset,
    setTopOffset,
    hasResponsiveModeProp,
    closeOnCareCreditVerification,
    hideBackButtonOnCareCreditVerification,
    hideBackButtonOnCareCreditCollect,

    // CareCredit additions
    careCreditData,
    careCreditChipStatus,
    isCareCreditLoading: isCareCreditLocationStatusLoading || isCareCreditDataLoading,

    /**
     * Care credit messaging flows can be reached by decoupled.
     */
    isCareCreditEnabled: showAnyCareCreditUI,
    isCareCreditRestrictedState,
    careCreditPaymentStatus,
    setCareCreditPaymentStatus,
    careCreditEnrollmentStatus,
    setCareCreditEnrollmentStatus,
    careCreditMessageType,
    setCareCreditMessageType,

    // temp values
    isServerDrivenV2Enabled,
  };
};
